import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { gpsTracking } from '@/services/gpsTracking';
import { selectiveAlarm } from '@/services/selectiveAlarm';
import { useToast } from '@/shared/toast';
import { useConfirm } from '@/shared/confirm';
import { useAuth } from '@/shared/AuthContext';
import { SelectiveUserListModal } from './SelectiveUserListModal';
import { SelectiveGpsMapModal } from './SelectiveGpsMapModal';
import { getGps } from './getGps';
import { useSelectiveResponseHandler } from './useSelectiveResponseHandler';
import { REFETCH_LIST_MS } from '@/shared/timing';
import type { ActiveSelective, ResponsesData } from './types';
import styles from './ActiveSelectiveItem.module.css';

/**
 * Card di una singola selettiva attiva: counter risposte, GPS tracking,
 * azioni Accetta/Rifiuta/Termina, sub-modal user list + mappa.
 * Embedded come child di ActiveSelectivesModal.
 *
 * La logica "accetto + gestione 409 AlreadyActiveOnAnother" e' candidata
 * a estrazione in un hook condiviso (useSelectiveResponseHandler) — vedi
 * task #16.
 */
export function ActiveSelectiveItem({ s }: { s: ActiveSelective }): React.JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const responseHandler = useSelectiveResponseHandler();
  const [tracking, setTracking] = useState(gpsTracking.getActive() === s.id);
  const [myResponseLocal, setMyResponseLocal] = useState<'Accepted' | 'Rejected' | null>(null);
  const [userListFilter, setUserListFilter] = useState<'accepted' | 'rejected' | 'noResponse' | null>(null);
  const [showMap, setShowMap] = useState(false);

  // Iscriviti ai cambiamenti dello stato tracking
  useEffect(() => {
    return gpsTracking.onChange(active => setTracking(active === s.id));
  }, [s.id]);

  // Recupera la risposta persistente dell'utente corrente (sopravvive a refresh).
  // useAuth invece di localStorage diretto (audit techlead #5).
  const { user: currentUser } = useAuth();
  const myId = currentUser?.id ?? null;

  const { data: responses } = useQuery<ResponsesData>({
    queryKey: ['selective.responses', s.id],
    queryFn: async () => (await api.get(`/selectives/${s.id}/responses`)).data,
    refetchInterval: REFETCH_LIST_MS,
    enabled: !!myId,
  });

  const myResponseFromServer: 'Accepted' | 'Rejected' | null = (() => {
    if (!responses || !myId) return null;
    if (responses.accepted.some(u => u.userId === myId)) return 'Accepted';
    if (responses.rejected.some(u => u.userId === myId)) return 'Rejected';
    return null;
  })();
  const myResponse = myResponseLocal ?? myResponseFromServer;

  const priorityColor =
    s.priority === 'Alta' ? '#dc3545' :
    s.priority === 'Bassa' ? '#28a745' : '#ff9800';
  const priorityLabel =
    s.priority === 'Alta' ? '🔴 Alta' :
    s.priority === 'Bassa' ? '🟢 Bassa' : '🟠 Media';

  // Gestione accept/reject delegata a useSelectiveResponseHandler hook:
  // centralizza 409 + GPS + invalidate queries (riusato da GlobalSelectiveAlert).
  const respond = useMutation({
    mutationFn: async (response: 'Accepted' | 'Rejected') => {
      const outcome = response === 'Accepted'
        ? await responseHandler.accept(s.id, getGps)
        : await responseHandler.reject(s.id);
      if (outcome === 'accepted') {
        setMyResponseLocal('Accepted');
        selectiveAlarm.stop();
      } else if (outcome === 'rejected') {
        setMyResponseLocal('Rejected');
        selectiveAlarm.stop();
      }
      // outcome 'cancelled' = l'utente ha annullato lo switch -> nessuna modifica
      // outcome 'error' = toast e' gia' stato mostrato dall'hook
      return outcome;
    },
  });

  const terminate = useMutation({
    mutationFn: async () => { await api.post(`/selectives/${s.id}/terminate`, { reason: 'Completata' }); },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['selectives.active'] });
      qc.invalidateQueries({ queryKey: ['selectives.history'] });
      qc.invalidateQueries({ queryKey: ['selectives.stats'] });
      if (gpsTracking.getActive() === s.id) gpsTracking.stop();
      toast.show('Intervento terminato e salvato nelle statistiche', 'success');
    },
    onError: () => toast.show('Errore nella terminazione', 'error'),
  });

  async function handleTerminateClick(): Promise<void> {
    const ok = await confirm({
      title: 'Termina intervento',
      message: `Vuoi terminare questo intervento "${s.type}"? Verrà salvato nelle statistiche e non sarà più attivo.`,
      confirmLabel: '🏁 Sì, termina',
      cancelLabel: 'Annulla',
      variant: 'warning',
      icon: '🏁',
    });
    if (ok) terminate.mutate();
  }

  // "Intervento non piu' necessario": avvisa chi ha accettato che possono
  // rientrare, MA la selettiva resta ATTIVA (a differenza di terminate non
  // viene rimossa dalla lista). Invalida la stessa query attiva cosi' il flag
  // noLongerNeededAt si aggiorna e il bottone diventa once-only.
  const noLongerNeeded = useMutation({
    mutationFn: async () => { await api.post(`/selectives/${s.id}/no-longer-needed`); },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['selectives.active'] });
      toast.show('Pompieri avvisati: intervento non più necessario', 'success');
    },
    onError: () => toast.show('Errore nell\'invio della segnalazione', 'error'),
  });

  const alreadyNotified = !!s.noLongerNeededAt;

  async function handleNoLongerNeededClick(): Promise<void> {
    const ok = await confirm({
      title: 'Intervento non più necessario',
      message: 'Avvisare chi ha accettato che l\'intervento non è più necessario? Riceveranno il segnale di fine, ma l\'intervento resterà ATTIVO finché non premi Termina.',
      confirmLabel: '🔕 Sì, avvisa',
      cancelLabel: 'Annulla',
      variant: 'warning',
      icon: '🔕',
    });
    if (ok) noLongerNeeded.mutate();
  }

  return (
    <div className={styles.card} style={{ borderLeft: `4px solid ${priorityColor}` }}>
      <h4 className={styles.title}>🚨 {s.type}</h4>
      <div className={styles.priorityLine} style={{ color: priorityColor }}>
        <strong>Priorità:</strong> {priorityLabel}
      </div>
      {s.callerInfo && <div className={styles.metaRow}><strong>Chiamante:</strong> {s.callerInfo}</div>}
      {s.recipient  && <div className={styles.metaRow}><strong>Destinatario:</strong> {s.recipient}</div>}
      {s.zone       && <div className={styles.metaRow}><strong>Zona:</strong> {s.zone}</div>}
      {s.notes      && <div className={styles.metaRow}><strong>Note:</strong> {s.notes}</div>}
      {s.createdByName && <div className={styles.metaRow}><strong>Inviata da:</strong> {s.createdByName}</div>}
      <div className={styles.createdAt}>
        <strong>Orario:</strong> {new Date(s.createdAt).toLocaleString('it-IT')}
      </div>

      {/* Counter risposte — bottoni cliccabili che aprono la lista utenti */}
      <div className={styles.countersRow}>
        <button onClick={() => setUserListFilter('accepted')}
          className={`${styles.counterBtn} ${styles.counterAccepted}`}>
          ✅ SI: {s.acceptedCount}
        </button>
        <button onClick={() => setUserListFilter('rejected')}
          className={`${styles.counterBtn} ${styles.counterRejected}`}>
          ❌ NO: {s.rejectedCount}
        </button>
        <button onClick={() => setUserListFilter('noResponse')}
          className={`${styles.counterBtn} ${styles.counterNoResponse}`}>
          ⏳ Nessuna risposta
        </button>
      </div>

      {/* Bottone mappa GPS posizioni in tempo reale */}
      <button onClick={() => setShowMap(true)} className={styles.mapBtn}>
        📍 Mappa Posizioni in Tempo Reale
      </button>

      {/* Indicatore GPS tracking attivo */}
      {tracking && (
        <div className={`${styles.statusBadge} ${styles.statusTracking}`}>
          📡 GPS tracking attivo (aggiornamento posizione ogni 10s)
        </div>
      )}

      {/* Indicatore risposta corrente */}
      {myResponse && (
        <div className={`${styles.statusBadge} ${myResponse === 'Accepted' ? styles.statusAccepted : styles.statusRejected}`}>
          {myResponse === 'Accepted' ? '✅ Hai accettato' : '❌ Hai rifiutato'}
        </div>
      )}

      {/* Azioni: ACCETTO / NON ACCETTO visibili SOLO se l'utente non ha
          ancora risposto (come legacy). "Termina" sempre visibile per i
          ruoli che possono terminare la selettiva. */}
      <div className={styles.actionsRow}>
        {!myResponse && (
          <>
            <button onClick={() => respond.mutate('Accepted')} disabled={respond.isPending}
              className={`${styles.respondBtn} ${styles.respondAccept}`}>
              ✅ ACCETTO
            </button>
            <button onClick={() => respond.mutate('Rejected')} disabled={respond.isPending}
              className={`${styles.respondBtn} ${styles.respondReject}`}>
              ❌ NON ACCETTO
            </button>
          </>
        )}
        <button onClick={handleTerminateClick} disabled={terminate.isPending}
          className={styles.terminateBtn}>
          🏁 Termina Intervento
        </button>
        {/* Sotto "Termina": avvisa che l'intervento non e' piu' necessario
            (la selettiva resta attiva). Once-only via noLongerNeededAt. */}
        <button onClick={handleNoLongerNeededClick}
          disabled={noLongerNeeded.isPending || alreadyNotified}
          className={styles.noLongerNeededBtn}>
          {alreadyNotified ? '🔕 Già avvisato' : '🔕 Intervento non più necessario'}
        </button>
      </div>

      {userListFilter && (
        <SelectiveUserListModal selectiveId={s.id} filter={userListFilter}
          onClose={() => setUserListFilter(null)} />
      )}
      {showMap && (
        <SelectiveGpsMapModal selectiveId={s.id} onClose={() => setShowMap(false)} />
      )}
    </div>
  );
}
